import React, { useEffect, useState } from "react";
import { api, formatDate, formatBirthday, CATEGORIAS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Pencil, Trash2, Plus, Mail, Phone, MapPin, Cake, Send, FileText } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { nombre: "", nombre_fiscal: "", rfc: "", email: "", telefono: "", direccion: "", tipo_proyecto: "Residencial", fecha_cumpleanos: "" };

export default function Clientes() {
  const [items, setItems] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/clientes"), api.get("/clientes-cumpleanos/proximos", { params: { dias: 30 } })]);
    setItems(a.data);
    setUpcoming(b.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...emptyForm, ...c, fecha_cumpleanos: c.fecha_cumpleanos || "" }); setOpen(true); };

  const save = async () => {
    if (!form.nombre) { toast.error("El nombre es obligatorio"); return; }
    try {
      const payload = { ...form };
      if (!payload.fecha_cumpleanos) delete payload.fecha_cumpleanos;
      if (editing) await api.put(`/clientes/${editing.id}`, payload);
      else await api.post("/clientes", payload);
      toast.success(editing ? "Cliente actualizado" : "Cliente creado");
      setOpen(false);
      load();
    } catch { toast.error("Error al guardar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este cliente?")) return;
    await api.delete(`/clientes/${id}`);
    toast.success("Eliminado");
    load();
  };

  const sendBirthday = async (id) => {
    try {
      const { data } = await api.post(`/clientes/${id}/enviar-cumpleanos`);
      if (data.sent) toast.success(`Email enviado a ${data.to}`);
      else toast.warning(data.reason || "No se pudo enviar");
    } catch (e) {
      toast.error("Error al enviar");
    }
  };

  return (
    <div className="space-y-8" data-testid="clientes-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="uppercase-label">Contactos</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Clientes</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Mantén tu directorio y envía felicitaciones de cumpleaños.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" data-testid="cliente-new">
          <Plus className="w-4 h-4 mr-2" /> Nuevo cliente
        </Button>
      </header>

      {upcoming.length > 0 && (
        <section className="border border-[#D4A373]/40 bg-[#D4A373]/10 rounded-lg p-5" data-testid="upcoming-birthdays">
          <div className="flex items-center gap-2 mb-3">
            <Cake className="w-4 h-4 text-[#8B5A33]" />
            <div className="font-display text-xl">Cumpleaños próximos (30 días)</div>
          </div>
          <div className="flex flex-wrap gap-3">
            {upcoming.map((c) => (
              <div key={c.id} className="bg-white border border-border rounded px-4 py-3 flex items-center gap-3" data-testid={`bday-card-${c.id}`}>
                <div>
                  <div className="font-medium text-sm">{c.nombre}</div>
                  <div className="text-xs text-muted-foreground">{formatBirthday(c.fecha_cumpleanos)} · en {c.dias_para_cumpleanos} días</div>
                </div>
                {c.email && (
                  <Button size="sm" variant="outline" onClick={() => sendBirthday(c.id)} data-testid={`bday-send-${c.id}`}>
                    <Send className="w-3 h-3 mr-1" /> Felicitar
                  </Button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 border border-dashed border-border rounded-lg p-10 text-center text-muted-foreground text-sm">
            Aún no hay clientes registrados.
          </div>
        )}
        {items.map((c) => (
          <div key={c.id} className="border border-border bg-white rounded-lg p-5 hover-lift" data-testid={`cliente-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-2xl leading-tight">{c.nombre}</div>
                <div className="uppercase-label mt-1">{c.tipo_proyecto}</div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`cliente-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`cliente-delete-${c.id}`}><Trash2 className="w-4 h-4 text-[#BC4749]" /></Button>
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-foreground/80">
              {c.nombre_fiscal && <div className="flex items-start gap-2"><FileText className="w-3.5 h-3.5 text-muted-foreground mt-0.5" /><span>{c.nombre_fiscal}</span></div>}
              {c.rfc && <div className="text-xs tabular text-muted-foreground ml-5">RFC: {c.rfc}</div>}
              {c.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-muted-foreground" />{c.email}</div>}
              {c.telefono && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-muted-foreground" />{c.telefono}</div>}
              {c.direccion && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-muted-foreground" />{c.direccion}</div>}
              {c.fecha_cumpleanos && <div className="flex items-center gap-2"><Cake className="w-3.5 h-3.5 text-muted-foreground" />Cumple {formatBirthday(c.fecha_cumpleanos)}</div>}
              <div className="text-xs text-muted-foreground pt-2 border-t border-border mt-3">Desde {formatDate(c.created_at)}</div>
            </div>
            {c.email && c.fecha_cumpleanos && (
              <Button size="sm" variant="outline" onClick={() => sendBirthday(c.id)} className="mt-3 w-full" data-testid={`cliente-send-bday-${c.id}`}>
                <Send className="w-3 h-3 mr-2" /> Enviar felicitación
              </Button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="cliente-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar cliente" : "Nuevo cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5"><Label>Nombre / contacto</Label><Input data-testid="cliente-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nombre fiscal / razón social</Label><Input data-testid="cliente-nombre-fiscal" value={form.nombre_fiscal || ""} onChange={(e) => setForm({ ...form, nombre_fiscal: e.target.value })} placeholder="Como aparece en SAT" /></div>
              <div className="space-y-1.5"><Label>RFC</Label><Input data-testid="cliente-rfc" value={form.rfc || ""} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} placeholder="XAXX010101000" maxLength={13} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email</Label><Input data-testid="cliente-email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Teléfono</Label><Input data-testid="cliente-telefono" value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Dirección</Label><Input data-testid="cliente-direccion" value={form.direccion || ""} onChange={(e) => setForm({ ...form, direccion: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo de proyecto</Label>
                <Select value={form.tipo_proyecto} onValueChange={(v) => setForm({ ...form, tipo_proyecto: v })}>
                  <SelectTrigger data-testid="cliente-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Fecha de cumpleaños</Label>
                <Input type="date" data-testid="cliente-cumple" value={form.fecha_cumpleanos || ""} onChange={(e) => setForm({ ...form, fecha_cumpleanos: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" data-testid="cliente-save">{editing ? "Actualizar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
